import { FiBarChart2 } from "react-icons/fi";
import { cn } from "utils/helpers";
import SkeletonBase from "newui/components/Skeleton";
import { useAverageScores } from "../../../../hooks/usePanelScoringQueries";

interface AverageScoringTableProps {
	rfxId: number;
	evalId: number;
	allSubmitted: boolean;
}

export const AverageScoringTable: React.FC<AverageScoringTableProps> = ({
	rfxId,
	evalId,
	allSubmitted,
}) => {
	const { data, isLoading, error } = useAverageScores(
		allSubmitted ? rfxId : undefined,
		allSubmitted ? evalId : undefined
	);

	if (!allSubmitted) {
		return (
			<div
				className="rounded-lg border border-dashed border-gray-300 p-6 text-center"
				data-testid="average-scoring-locked"
			>
				<FiBarChart2 size={24} className="mx-auto mb-2 text-gray-300" aria-hidden="true" />
				<p className="text-sm text-gray-400">
					Average scores will appear once all panel members have submitted.
				</p>
			</div>
		);
	}

	if (isLoading) {
		return (
			<div className="space-y-2" data-testid="average-scoring-loading">
				{[1, 2].map((i) => (
					<SkeletonBase key={i} startColor="lightgray" width="100%" height="48px" className="!rounded-lg" />
				))}
			</div>
		);
	}

	if (error || !data) {
		return (
			<p className="text-sm text-red-600" data-testid="average-scoring-error">
				Failed to load average scores.
			</p>
		);
	}

	const { suppliers, scoring_method, total_members } = data;

	if (suppliers.length === 0) {
		return (
			<div
				className="rounded-lg border border-dashed border-gray-300 p-6 text-center text-gray-400 text-sm"
				data-testid="average-scoring-empty"
			>
				No scoring data available.
			</div>
		);
	}

	return (
		<div data-testid="average-scoring-table">
			<div className="mb-3 flex items-center justify-between">
				<h4 className="text-sm font-medium text-gray-700">
					Average Scores
				</h4>
				<span className="text-xs text-gray-500">
					{scoring_method} · {total_members} member{total_members !== 1 ? "s" : ""}
				</span>
			</div>
			<div className="space-y-4">
				{suppliers.map((supplier) => (
					<div
						key={supplier.invite_id}
						className="rounded-lg border border-gray-200 bg-white"
						data-testid={`average-supplier-${supplier.invite_id}`}
					>
						<div className="flex items-center justify-between border-b border-gray-100 px-4 py-2.5">
							<span className="text-sm font-medium text-gray-900">
								{supplier.supplier_name}
							</span>
							{supplier.weighted_total !== null && (
								<span
									className={cn(
										"inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold",
										"bg-[#e8eeff] text-[#0031DD]"
									)}
								>
									<FiBarChart2 size={10} aria-hidden="true" />
									{supplier.weighted_total.toFixed(1)}
								</span>
							)}
						</div>
						<div className="overflow-x-auto">
							<table className="w-full border-collapse text-sm">
								<thead>
									<tr className="text-left text-xs text-gray-400">
										<th className="px-4 py-2 font-medium">Criterion</th>
										<th className="px-4 py-2 font-medium text-right w-20">Weight</th>
										<th className="px-4 py-2 font-medium text-right w-24">Avg Score</th>
										<th className="px-4 py-2 font-medium text-right w-20">Responses</th>
									</tr>
								</thead>
								<tbody>
									{supplier.criteria.map((c) => (
										<tr
											key={c.rfx_criteria_id}
											className="border-t border-gray-50"
										>
											<td className="px-4 py-2 text-gray-700">
												{c.criterion_name}
											</td>
											<td className="px-4 py-2 text-right text-gray-500 tabular-nums">
												{c.criterion_weight}%
											</td>
											<td className="px-4 py-2 text-right font-medium tabular-nums">
												{c.average_score !== null
													? c.average_score.toFixed(1)
													: "—"}
											</td>
											<td className="px-4 py-2 text-right text-gray-500 tabular-nums">
												{c.score_count}
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					</div>
				))}
			</div>
		</div>
	);
};
